/**
 * „ZEO zu Aufgaben befragen“ (Aufgabenverwaltung 2.0) – Bereich der Aufgabenseite, kein zweites Chatsystem: dieselbe ZEO-/Mittwald-Anbindung,
 * nur lesend, Kontext = ausgewählte Aufgaben ODER alle Aufgaben des gefilterten Klienten. Antworten trennen belegte Fakten, Schlussfolgerungen
 * und Vorschläge und verweisen auf konkrete Aufgaben/Dokumente. Aktionen bereiten nur vor – geschrieben wird erst nach eigener Vorschau.
 */
import { useState } from 'react';
import { Bot, ChevronDown, ChevronUp, FileText, Loader2, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { betreuungAPI } from '@/api/betreuungAPI';
import { toast } from 'sonner';

const BEISPIELE = ['Welche dieser Aufgaben sind doppelt?', 'Welche Aufgaben kann ich vermutlich abschließen?', 'Welche Fristen sind dringend?',
  'Welche Aufgaben sollte ich sinnvoll zusammenführen?', 'Was ist bei diesem Klienten derzeit noch offen?',
  'Welche Aufgaben haben sich durch neu eingegangene Dokumente möglicherweise erledigt?', 'Was hat sich seit der letzten Bearbeitung verändert?'];

const AKTION_LABEL = { auswaehlen: 'Aufgaben auswählen', zusammenfuehren: 'Zusammenführen prüfen', erledigen: 'Erledigen prüfen',
  unteraufgaben: 'Unteraufgaben vorschlagen', verknuepfen: 'Dokument verknüpfen' };

function Aussagen({ titel, farbe, eintraege, auswahl, onToggle, onOpen, testid }) {
  if (!eintraege?.length) return null;
  return (
    <div data-testid={testid}>
      <p className={`text-xs font-semibold uppercase ${farbe}`}>{titel}</p>
      <ul className="space-y-1 mt-1">
        {eintraege.map((e, i) => (
          <li key={i} className="text-sm text-slate-700">
            {e.text}
            <span className="inline-flex flex-wrap gap-1 ml-1">
              {e.aufgaben.map(a => (
                <span key={a.id} className="inline-flex items-center gap-1 text-xs bg-blue-50 border border-blue-200 rounded px-1.5" data-testid={`zeo-ref-${a.id}`}>
                  <input type="checkbox" checked={auswahl.has(a.id)} onChange={() => onToggle(a)} title="auswählen" />
                  <button className="hover:underline" onClick={() => onOpen(a.id)}>{a.titel}</button>
                </span>
              ))}
              {e.dokumente.map(d => (
                <span key={d.id} className="inline-flex items-center gap-1 text-xs bg-slate-100 rounded px-1.5" title={`Quelle: Dokument ${d.id}`}><FileText className="w-3 h-3" />{d.titel}</span>
              ))}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Antwort von „ZEO zu Aufgaben befragen“ – gemeinsam für /aufgaben und den großen ZEO-Chat: belegt / Schlussfolgerung / Vorschlag mit Aufgaben-
 * und Dokumentreferenzen, fehlende Informationen, vorbereitete Aktionen (Schreibaktionen nur über Vorschau + Bestätigung). */
export function ZeoFrageAntwort({ antwort, auswahl, onToggle, onOpen, onSelect, onMerge, onErledigen, onReload, ohneKurzantwort = false }) {
  const [aktion, setAktion] = useState(null);           // Vorschau einer Schreibaktion (unteraufgaben | verknuepfen)
  const [titelWahl, setTitelWahl] = useState([]);

  const ausfuehren = (a) => {
    const ids = a.aufgaben.map(x => x.id);
    if (a.typ === 'auswaehlen') onSelect(a.aufgaben);
    else if (a.typ === 'zusammenfuehren') onMerge(a.aufgaben);
    else if (a.typ === 'erledigen') onErledigen(a.aufgaben);
    else { setAktion({ ...a, ids }); setTitelWahl(a.unteraufgaben || []); }
  };

  const aktionBestaetigen = async () => {
    try {
      if (aktion.typ === 'unteraufgaben') {
        const r = await betreuungAPI.unteraufgabenAnlegen(aktion.ids[0], titelWahl);
        const neu = r.ergebnisse.filter(x => x.neu).length;
        (r.vollstaendig ? toast.success : toast.error)(`${neu} Unteraufgabe(n) angelegt${r.ergebnisse.length - neu ? `, ${r.ergebnisse.length - neu} bestanden schon` : ''}`);
      } else {
        const r = await betreuungAPI.dokumentMitAufgabeVerknuepfen(aktion.ids[0], aktion.dokument.id);
        (r.ok ? toast.success : toast.error)(r.neu ? 'Dokument verknüpft (zurückgelesen)' : 'Dokument war bereits verknüpft');
      }
      setAktion(null);
      onReload?.();
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
            <div className="border-t pt-3 space-y-3" data-testid="zeo-antwort">
              <p className="text-xs text-slate-500">Berücksichtigt: {antwort.umfang}. Es wurde keine Aufgabe geändert.{antwort.ki.hinweis ? ` ${antwort.ki.hinweis}` : ''}</p>
              {antwort.kurzantwort && !ohneKurzantwort && <p className="text-sm text-slate-800">{antwort.kurzantwort}</p>}
              <Aussagen titel="Belegt" farbe="text-green-700" eintraege={antwort.fakten} auswahl={auswahl} onToggle={onToggle} onOpen={onOpen} testid="zeo-fakten" />
              <Aussagen titel="Schlussfolgerung" farbe="text-blue-700" eintraege={antwort.schlussfolgerungen} auswahl={auswahl} onToggle={onToggle} onOpen={onOpen} testid="zeo-schluss" />
              <Aussagen titel="Vorschlag" farbe="text-purple-700" eintraege={antwort.vorschlaege} auswahl={auswahl} onToggle={onToggle} onOpen={onOpen} testid="zeo-vorschlaege" />
              {antwort.fehlende_informationen.length > 0 && (
                <div><p className="text-xs font-semibold uppercase text-amber-700">Fehlende Informationen</p>
                  <ul className="list-disc ml-5 text-sm text-slate-700">{antwort.fehlende_informationen.map((f, i) => <li key={i}>{f}</li>)}</ul></div>
              )}
              {antwort.aktionen.length > 0 && (
                <div className="flex flex-wrap gap-2" data-testid="zeo-aktionen">
                  {antwort.aktionen.map((a, i) => (
                    <Button key={i} size="sm" variant="outline" onClick={() => ausfuehren(a)} title={a.begruendung} data-testid={`zeo-aktion-${a.typ}`}>
                      {AKTION_LABEL[a.typ]}: {a.aufgaben.map(x => x.titel).join(', ').slice(0, 60)}
                    </Button>
                  ))}
                </div>
              )}
              {aktion && (
                <div className="border border-purple-300 bg-purple-50/50 rounded p-3 space-y-2" data-testid="zeo-aktion-vorschau">
                  {aktion.typ === 'unteraufgaben' ? (
                    <>
                      <p className="text-sm">Unter „{aktion.aufgaben[0].titel}“ anlegen:</p>
                      {(aktion.unteraufgaben || []).map(t => (
                        <label key={t} className="flex items-center gap-2 text-sm">
                          <input type="checkbox" checked={titelWahl.includes(t)} onChange={() => setTitelWahl(p => p.includes(t) ? p.filter(x => x !== t) : [...p, t])} />{t}
                        </label>
                      ))}
                    </>
                  ) : (
                    <p className="text-sm">Dokument „{aktion.dokument.titel}“ mit Aufgabe „{aktion.aufgaben[0].titel}“ verknüpfen?</p>
                  )}
                  <div className="flex gap-2">
                    <Button size="sm" onClick={aktionBestaetigen} disabled={aktion.typ === 'unteraufgaben' && !titelWahl.length} data-testid="zeo-aktion-bestaetigen">Bestätigen</Button>
                    <Button size="sm" variant="outline" onClick={() => setAktion(null)}>Abbrechen</Button>
                  </div>
                </div>
              )}
              {antwort.kontext.length > 0 && (
                <details className="text-xs text-slate-500"><summary>Berücksichtigte Aufgaben ({antwort.kontext.length})</summary>
                  <ul className="mt-1 space-y-0.5">{antwort.kontext.map(a => (
                    <li key={a.id}><input type="checkbox" className="mr-1" checked={auswahl.has(a.id)} onChange={() => onToggle(a)} />
                      <button className="hover:underline" onClick={() => onOpen(a.id)}>{a.titel}</button> · {a.status}</li>
                  ))}</ul>
                </details>
              )}
            </div>
  );
}

export default function AufgabenZeoFrage({ offen, onOffen, klientId, klientName, auswahl, onToggle, onSelect, onMerge, onErledigen, onOpen, onReload, modus, onModus }) {
  const [frage, setFrage] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [antwort, setAntwort] = useState(null);

  const auswahlKlient = (() => { const k = new Set([...auswahl.values()].map(a => a.klient_id)); return k.size === 1 ? [...k][0] : null; })();
  const fragen = async (text) => {
    const f = (text ?? frage).trim();
    if (!f) return;
    if (modus === 'auswahl' && (!auswahl.size || !auswahlKlient)) { toast.error('Bitte Aufgaben genau eines Klienten auswählen.'); return; }
    if (modus === 'klient' && !klientId) { toast.error('Bitte zuerst nach einem Klienten filtern.'); return; }
    setLaeuft(true); setAntwort(null);
    try {
      setAntwort(await betreuungAPI.zeoFrageAufgaben({ frage: f, modus, klient_id: modus === 'klient' ? klientId : auswahlKlient, aufgabe_ids: modus === 'auswahl' ? [...auswahl.keys()] : [] }));
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <div className="bg-white border border-purple-200 rounded-xl" data-testid="zeo-frage-bereich">
      <button className="w-full flex items-center justify-between px-4 py-3 text-left" onClick={() => onOffen(!offen)} data-testid="zeo-frage-toggle">
        <span className="flex items-center gap-2 font-medium text-purple-800"><Bot className="w-4 h-4" />ZEO zu Aufgaben befragen</span>
        {offen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      {offen && (
        <div className="px-4 pb-4 space-y-3">
          <div className="flex flex-wrap gap-4 text-sm">
            <label className="flex items-center gap-1">
              <input type="radio" name="zeo-modus" checked={modus === 'auswahl'} onChange={() => onModus('auswahl')} data-testid="zeo-modus-auswahl" />
              Nur ausgewählte Aufgaben ({auswahl.size})
            </label>
            <label className={`flex items-center gap-1 ${klientId ? '' : 'opacity-50'}`}>
              <input type="radio" name="zeo-modus" checked={modus === 'klient'} disabled={!klientId} onChange={() => onModus('klient')} data-testid="zeo-modus-klient" />
              Alle Aufgaben {klientName ? `von ${klientName}` : 'des Klienten (erst Klient filtern)'}
            </label>
          </div>
          <div className="flex gap-2">
            <Textarea rows={2} value={frage} onChange={e => setFrage(e.target.value)} placeholder="Frage an ZEO … (ZEO ändert dabei keine Daten)"
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); fragen(); } }} data-testid="zeo-frage-eingabe" />
            <Button onClick={() => fragen()} disabled={laeuft} data-testid="zeo-frage-senden">{laeuft ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}</Button>
          </div>
          <div className="flex flex-wrap gap-1">
            {BEISPIELE.map(b => <button key={b} className="text-xs border rounded-full px-2 py-0.5 text-slate-600 hover:bg-slate-50" onClick={() => { setFrage(b); fragen(b); }}>{b}</button>)}
          </div>
          {antwort && (
            <div>
              <ZeoFrageAntwort antwort={antwort} auswahl={auswahl} onToggle={onToggle} onOpen={onOpen} onSelect={onSelect} onMerge={onMerge}
                onErledigen={onErledigen} onReload={onReload} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
